import { useEffect, useRef, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { useAuth } from "./context/AuthContext";
import { consumeSocialOAuthCallback, enrichSocialLoginProfile } from "./utils/socialOAuthCallback";
import Header from "./Conponets/Header";
import Footer from "./Conponets/Footer";
import Home from "./Conponets/Home";
import Register from "./Pages/Register";
import Login from "./Pages/Login";
import RecoveryPassword from "./Pages/RecoveryPassword";
import "./App.css";

import Sidebar from "./Conponets/Sidebar";
import ScrollToTop from "./ScrollToTop";
import NotFound from "./Pages/NotFound";
import PrivateRoute from "./Routes/PrivateRoute";
import CompanyDetailsPage from "./Pages/CompanyDetailsPage";
import JobDetails from "./Pages/JobDetails";
import Employers from "./Pages/Employers";
import JobList from "./Pages/JobList";
import RouteSEO from "./components/RouteSEO";
import GoogleMarketingScripts from "./components/GoogleMarketingScripts";
import HomePageSEO from "./components/HomePageSEO";
import LegacyJobRedirect from "./Pages/LegacyJobRedirect";
import RouteFallback from "./components/RouteFallback";
import * as Lazy from "./Routes/lazyPages";
// "build 04-09-2025"
console.log("Date:20-02-2026,time:-18:05");
function LayoutWrapper() {
  const location = useLocation();
  const navigate = useNavigate();
  const { login: authLogin, updateProfileImage, updateName } = useAuth();
  const { t } = useTranslation("global");
  const oauthResultRef = useRef(null);
  const oauthHandledRef = useRef(false);

  if (oauthResultRef.current === null) {
    oauthResultRef.current = consumeSocialOAuthCallback(location.search);
  }

  useEffect(() => {
    if (oauthHandledRef.current) {
      return;
    }

    const result = oauthResultRef.current;
    if (!result) {
      return;
    }

    oauthHandledRef.current = true;

    const finishSocialLogin = async () => {
      if (result.error) {
        toast.error(result.error);
        oauthResultRef.current = null;
        return;
      }

      if (!result.persisted) {
        oauthResultRef.current = null;
        return;
      }

      const profileData = await enrichSocialLoginProfile(result.role);
      authLogin();

      if (profileData) {
        const profileImg = localStorage.getItem("profileImage");
        if (profileImg && typeof updateProfileImage === "function") {
          updateProfileImage(profileImg);
        }

        const firstName =
          profileData.first_name || localStorage.getItem("first_name") || "";
        const lastName =
          profileData.last_name || localStorage.getItem("last_name") || "";
        if (typeof updateName === "function") {
          updateName(firstName, lastName);
        }
      }

      if (result.blockedUnverified) {
        await Swal.fire({
          title: t("header.Account_Not_Verified"),
          text: t("header.Your_account_is_not_verified_by_the_admin"),
          icon: "error",
          confirmButtonText: t("header.ok"),
        });
        navigate("/", { replace: true });
        oauthResultRef.current = null;
        return;
      }

      if (result.redirectPath && location.pathname !== result.redirectPath) {
        navigate(result.redirectPath, { replace: true });
      }

      oauthResultRef.current = null;
    };

    finishSocialLogin();
    // Run once for the OAuth callback only; do not re-run on route changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const noLayoutRoutes = [
    "/start-test",
    "/apply-test",
    "/test-result",
    "/checkout",
    "/payment-success",
    "/payment-failed",
  ];
  const hideLayout = noLayoutRoutes.some((route) =>
    location.pathname.startsWith(route),
  );
  const sidebarRoutes = [
    "/candidate-profile",
    "/certificates-scores",
    "/skill-assessments-tests",
    "/job-search",
    "/companies-list",
    "/companies-list",
    "/your-job-posts",
    "/employer-wallet",
    "/employer-dashboard",
    "/manage-applicants",
    "/bookmark-candidate",
    "/candidates-search",
    "/all-applicants-list",
    "/create-recruiters",
    "/create-assessment",
    "/request-plan",
    "/manage-recruiter",
    "/manage-assessment",
    "/messaging-system",
    "/change-password",
    "/notifications",
    "/company-profile",
    "/assessment-details",
    "/job-details-form",
    "/job-details-list",
    "/resume-builder",
    // "/job-details",
    "/candidate-dashboard",
    "/view-invoice",
    "/chat-messaging-system",
    "/applied-jobs-list",
    "/activity-timeline",
    // "/candidates-details",
    "/manage-job-application",
    "/application-management",
    "/job-alert",
  ];

  const showSidebar = sidebarRoutes.some((route) =>
    location.pathname.startsWith(route),
  );

  const bgColor = showSidebar ? "#fff" : "#f0f5f7";
  const showFooter = !hideLayout && !showSidebar;
  return (
    <>
      <RouteSEO />
      <HomePageSEO />
      <GoogleMarketingScripts />
      {!hideLayout && <Header bgColor={bgColor} />}
      {showSidebar && <Sidebar />}

      <main id="main-content" role="main">
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/verification" element={<Lazy.Varification />} />
        <Route path="/account-verified" element={<Lazy.AccountVerified />} />
        <Route path="/verified-cancel" element={<Lazy.VerifiedCancel />} />
        <Route path="/register" element={<Register />} />
        <Route path="/recovery-password" element={<RecoveryPassword />} />
        <Route path="/forgot-password" element={<RecoveryPassword />} />
        <Route path="/verify-otp" element={<Lazy.SendOtp />} />
        <Route path="/employer-register" element={<Lazy.EmployerRegister />} />
        <Route path="/employer-login" element={<Lazy.EmployerLogin />} />
        <Route path="/email-verification" element={<Lazy.EmailOTPVerification />} />
        <Route path="/contact-us" element={<Lazy.ContactUs />} />
        <Route path="/privacy-policy" element={<Lazy.PrivecyPolicy />} />
        <Route path="/terms-condition" element={<Lazy.TearmCondition />} />
        <Route path="/companies" element={<Employers />} />
        <Route path="/faq/:type" element={<Lazy.Faq />} />{" "}
        <Route
          path="/applied-candidate-list"
          element={<Lazy.EmployerCandinateList />}
        />
        <Route path="/applicants-details" element={<Lazy.ApplicantsDetails />} />
        <Route path="/about-us" element={<Lazy.AboutUs />} />
        <Route path="/blog" element={<Lazy.Blog />} />
        <Route path="/payment-success" element={<Lazy.PaymentSuccess />} />
        <Route path="/payment-failed" element={<Lazy.PaymentFailed />} />
        <Route path="/blogDetails/:id" element={<Lazy.BlogDetails />} />
        <Route path="/employer-home" element={<Lazy.EmployerHomePage />} />
        <Route
          path="/employer-basic-info"
          element={<Lazy.EmployerBasicInformation />}
        />
        <Route path="/jobs" element={<JobList />} />
        <Route path="/job-details/:id" element={<LegacyJobRedirect />} />
        <Route
          path="/custom-resume-cover-letter"
          element={<Lazy.CustomResumeCoverLatter />}
        />
        <Route path="/company-details" element={<Lazy.CompanyDetailsInfo />} />
        <Route path="/add-plan" element={<Lazy.AddPlan />} />
        <Route path="/add-on-pack" element={<Lazy.AddOnPack />} />
        {/* Protected Routes */}
        <Route
          path="/job-details-form/:id"
          element={
            <PrivateRoute allowedRoles={["Recruiter", "Company"]}>
              <Lazy.JobDetailsForm />
            </PrivateRoute>
          }
        />
        <Route
          path="/job-details-form"
          element={
            <PrivateRoute allowedRoles={["Recruiter", "Company"]}>
              <Lazy.JobDetailsForm />
            </PrivateRoute>
          }
        />
        <Route
          path="/profile-basic-info"
          element={
            <PrivateRoute allowedRoles={["JobSeeker"]}>
              <Lazy.MyProfile />
            </PrivateRoute>
          }
        />
        <Route
          path="/candidate-profile"
          element={
            <PrivateRoute allowedRoles={["JobSeeker"]}>
              <Lazy.CandidateProfile />
            </PrivateRoute>
          }
        />
        <Route
          path="/resume-builder"
          element={
            <PrivateRoute allowedRoles={["JobSeeker"]}>
              <Lazy.ResumeBuilder />
            </PrivateRoute>
          }
        />
        <Route
          path="/employer-dashboard"
          element={
            <PrivateRoute allowedRoles={["Recruiter", "Company"]}>
              <Lazy.EmployerDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/skill-assessments-tests"
          element={
            <PrivateRoute>
              <Lazy.SkillAssementTestPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/certificates-scores"
          element={
            <PrivateRoute>
              <Lazy.CertificateScorePage />
            </PrivateRoute>
          }
        />
        <Route
          path="/job-alert"
          element={
            <PrivateRoute>
              <Lazy.JobAlert />
            </PrivateRoute>
          }
        />
        <Route
          path="/activity-timeline"
          element={
            <PrivateRoute  allowedRoles={["JobSeeker"]}>
              <Lazy.ActivityTimeline />
            </PrivateRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <PrivateRoute>
              <Lazy.Notifications />
            </PrivateRoute>
          }
        />
        <Route
          path="/application-management"
          element={
            <PrivateRoute>
              <Lazy.ApplicationManagement />
            </PrivateRoute>
          }
        />
        <Route
          path="/applied-jobs-list"
          element={
            <PrivateRoute allowedRoles={["Recruiter", "Company"]}>
              <Lazy.AppliedJobList />
            </PrivateRoute>
          }
        />
        <Route
          path="/chat-messaging-system"
          element={
            <PrivateRoute allowedRoles={["JobSeeker"]}>
              <Lazy.ChatMassageSystem />
            </PrivateRoute>
          }
        />
        <Route path="/:companySlug" element={<CompanyDetailsPage />} />
       <Route path="/job/:jobSlug" element={<JobDetails />} />
        <Route
          path="/job-details-list"
          element={
            <PrivateRoute>
              <Lazy.JobDetailsList />
            </PrivateRoute>
          }
        />
        <Route
          path="/candidate-dashboard"
          element={
            <PrivateRoute allowedRoles={["JobSeeker"]}>
              <Lazy.CandidateDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/manage-job-application"
          element={
            <PrivateRoute allowedRoles={["JobSeeker"]}>
              <Lazy.ManagesJobApplication />
            </PrivateRoute>
          }
        />
        <Route
          path="/your-job-posts"
          element={
            <PrivateRoute allowedRoles={["Recruiter", "Company"]}>
              <Lazy.YourJobPosts />
            </PrivateRoute>
          }
        />
        <Route
          path="/employer-wallet"
          element={
            <PrivateRoute allowedRoles={["Recruiter", "Company"]}>
              <Lazy.EmployerWallet />
            </PrivateRoute>
          }
        />
        <Route
          path="/checkout"
          element={
            <PrivateRoute allowedRoles={["Recruiter", "Company"]}>
              <Lazy.Checkout />
            </PrivateRoute>
          }
        />
        {/* <Route
          path="/employer-wallet"
          element={
            <PrivateRoute>
              <Lazy.EmployerWallet />
            </PrivateRoute>
          }
        /> */}
        <Route
          path="/view-invoice/:id"
          element={
            <PrivateRoute>
              <Lazy.InvoiceView />
            </PrivateRoute>
          }
        />
        <Route
          path="/setting"
          element={
            <PrivateRoute>
              <Lazy.Setting />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/google-marketing"
          element={
            <PrivateRoute allowedRoles={["Admin"]}>
              <Lazy.GoogleMarketingConfig />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/home-page-seo"
          element={
            <PrivateRoute allowedRoles={["Admin"]}>
              <Lazy.HomePageSeoConfig />
            </PrivateRoute>
          }
        />
        <Route
          path="/manage-applicants"
          element={
            <PrivateRoute>
              <Lazy.EmployerFilterCandinateList />
            </PrivateRoute>
          }
        />
        <Route
          path="/bookmark-candidate"
          element={
            <PrivateRoute allowedRoles={["Recruiter", "Company"]}>
              <Lazy.EmployerShortListCandinate />
            </PrivateRoute>
          }
        />
        <Route
          path="/candidates-search"
          element={
            <PrivateRoute allowedRoles={["Recruiter", "Company"]}>
              <Lazy.CandinatesList />
            </PrivateRoute>
          }
        />
        <Route
          path="/all-applicants-list"
          element={
            <PrivateRoute allowedRoles={["Recruiter", "Company"]}>
              <Lazy.ManagesApplicants />
            </PrivateRoute>
          }
        />
        <Route
          path="/create-recruiters"
          element={
            <PrivateRoute>
              <Lazy.CreateRecruiters />
            </PrivateRoute>
          }
        />
        <Route
          path="/create-assessment"
          element={
            <PrivateRoute>
              <Lazy.CreateAssement />
            </PrivateRoute>
          }
        />
        <Route
          path="/request-plan"
          element={
            <PrivateRoute>
              <Lazy.OfferContact />
            </PrivateRoute>
          }
        />
        <Route
          path="/manage-recruiter"
          element={
            <PrivateRoute allowedRoles={["Recruiter", "Company"]}>
              <Lazy.RecruiterLists />
            </PrivateRoute>
          }
        />
        <Route
          path="/manage-assessment"
          element={
            <PrivateRoute allowedRoles={["Recruiter", "Company"]}>
              <Lazy.ManagesAssement />
            </PrivateRoute>
          }
        />
        <Route
          path="/messaging-system"
          element={
            <PrivateRoute allowedRoles={["Recruiter", "Company"]}>
              <Lazy.MassagingSystem />
            </PrivateRoute>
          }
        />
        <Route
          path="/company-profile"
          element={
            <PrivateRoute allowedRoles={["Recruiter", "Company"]}>
              <Lazy.EmployerProfile />
            </PrivateRoute>
          }
        />
        <Route
          path="/assessment-details"
          element={
            <PrivateRoute>
              <Lazy.AssessmentDetails />
            </PrivateRoute>
          }
        />
        <Route
          path="/change-password"
          element={
            <PrivateRoute>
              <Lazy.ChangePassword />
            </PrivateRoute>
          }
        />
        <Route
          path="/candidates-details"
          element={
            <PrivateRoute>
              <Lazy.CandinateProfileDetails />
            </PrivateRoute>
          }
        />
        <Route
          path="/job-search"
          element={
            <PrivateRoute allowedRoles={["JobSeeker"]}>
              <Lazy.JobSearch />
            </PrivateRoute>
          }
        ></Route>
        <Route
          path="/apply-test"
          element={
            <PrivateRoute>
              <Lazy.ApplyTest />
            </PrivateRoute>
          }
        ></Route>
        <Route
          path="/start-test"
          element={
            <PrivateRoute>
              <Lazy.StartTest />
            </PrivateRoute>
          }
        ></Route>
        <Route
          path="/test-result"
          element={
            <PrivateRoute>
              <Lazy.TestResult />
            </PrivateRoute>
          }
        ></Route>
        <Route path="/companies-list" element={<Lazy.Companies />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      </main>
      {showFooter && <Footer />}
    </>
  );
}

function App() {
  return (
    <>
      <BrowserRouter basename="/jobPortal">
        <ScrollToTop />
        <LayoutWrapper />
      </BrowserRouter>
    </>
  );
}

export default App;
